import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, ConfirmModal, DataTable, Toast } from '../ui';
import Drawer from './Drawer';
import {
  listFamilies,
  getFamily,
  listAttributes,
  createFamily,
  updateFamily,
  deleteFamily,
  replaceFamilyAttributes,
} from '../../services/pimApi';

const blankForm = () => ({
  code: '',
  label: '',
  attribute_as_label_code: '',
  attribute_as_image_code: '',
  attributes: [], // {attribute_code, label, type, is_required, is_required_per_channel}
});

export default function FamiliesPanel() {
  const [rows, setRows] = useState([]);
  const [allAttributes, setAllAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [toast, setToast] = useState(null);
  const [formError, setFormError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, a] = await Promise.all([listFamilies(), listAttributes()]);
      setRows(f);
      setAllAttributes(a);
    } catch (e) {
      setToast({ message: 'Failed to load families', error: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const flash = (message, error = false) => {
    setToast({ message, error });
    setTimeout(() => setToast(null), 3000);
  };

  const openNew = () => {
    setEditing(blankForm());
    setIsNew(true);
    setFormError(null);
  };

  const openEdit = async (row) => {
    try {
      const full = await getFamily(row.code);
      setEditing({
        id: full.id,
        version: full.version,
        code: full.code,
        label: full.label,
        attribute_as_label_code: full.attribute_as_label_code || '',
        attribute_as_image_code: full.attribute_as_image_code || '',
        attributes: (full.attributes || []).map((a) => ({
          attribute_code: a.attribute_code,
          label: a.label,
          type: a.type,
          is_required: a.is_required,
          is_required_per_channel: a.is_required_per_channel,
        })),
      });
      setIsNew(false);
      setFormError(null);
    } catch (e) {
      flash('Failed to load family', true);
    }
  };

  const setField = (key, value) => setEditing((f) => ({ ...f, [key]: value }));

  const inFamily = (code) => editing.attributes.some((a) => a.attribute_code === code);
  const toggleAttr = (attr) => {
    if (inFamily(attr.code)) {
      setField('attributes', editing.attributes.filter((a) => a.attribute_code !== attr.code));
    } else {
      setField('attributes', [
        ...editing.attributes,
        {
          attribute_code: attr.code,
          label: attr.label,
          type: attr.type,
          is_required: attr.is_required_default,
          is_required_per_channel: false,
        },
      ]);
    }
  };
  const updateAttrFlag = (code, key, value) =>
    setField(
      'attributes',
      editing.attributes.map((a) => (a.attribute_code === code ? { ...a, [key]: value } : a)),
    );

  const save = async () => {
    if (!editing.code.trim() || !editing.label.trim()) {
      setFormError('Code and label are required.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      let famId = editing.id;
      const body = {
        label: editing.label.trim(),
        attribute_as_label_code: editing.attribute_as_label_code || null,
        attribute_as_image_code: editing.attribute_as_image_code || null,
      };
      if (isNew) {
        const created = await createFamily({ code: editing.code.trim(), ...body });
        famId = created.id;
      } else {
        await updateFamily(editing.id, editing.version, body);
      }
      await replaceFamilyAttributes(
        famId,
        editing.attributes.map((a, i) => ({
          attribute_code: a.attribute_code,
          is_required: a.is_required,
          is_required_per_channel: a.is_required_per_channel,
          sort_order: i,
        })),
      );
      flash('Family saved.');
      setEditing(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setFormError(typeof detail === 'string' ? detail : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setSaving(true);
    try {
      await deleteFamily(confirmDelete.id);
      flash(`Deleted ${confirmDelete.code}.`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      flash(e?.response?.data?.detail || 'Delete failed', true);
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      { key: 'code', header: 'Code', render: (r) => <code>{r.code}</code> },
      { key: 'label', header: 'Label' },
      { key: 'attribute_count', header: 'Attributes', align: 'right' },
      { key: 'product_count', header: 'Products', align: 'right' },
      {
        key: 'actions',
        header: '',
        align: 'right',
        render: (r) => (
          <>
            <Button size="sm" variant="secondary" onClick={() => openEdit(r)}>
              Edit
            </Button>{' '}
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(r)}>
              Delete
            </Button>
          </>
        ),
      },
    ],
    [openEdit],
  );

  const selectedAttrs = editing ? editing.attributes : [];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button onClick={openNew}>+ New family</Button>
      </div>
      <DataTable columns={columns} rows={rows} rowKey={(r) => r.id} loading={loading} />

      {editing && (
        <Drawer
          title={isNew ? 'New family' : `Edit ${editing.code}`}
          sub="A product definition: which attributes appear and which are required"
          onClose={() => setEditing(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setEditing(null)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save family'}
              </Button>
            </>
          }
        >
          {formError && <div className="pim-field-error" style={{ marginBottom: 12 }}>{formError}</div>}

          <div className="pim-row">
            <div className="pim-field">
              <label>Code</label>
              <input type="text" value={editing.code} disabled={!isNew} onChange={(e) => setField('code', e.target.value)} />
            </div>
            <div className="pim-field">
              <label>Label</label>
              <input type="text" value={editing.label} onChange={(e) => setField('label', e.target.value)} />
            </div>
          </div>

          <div className="pim-row">
            <div className="pim-field">
              <label>Label attribute</label>
              <select value={editing.attribute_as_label_code} onChange={(e) => setField('attribute_as_label_code', e.target.value)}>
                <option value="">(none)</option>
                {selectedAttrs.map((a) => (
                  <option key={a.attribute_code} value={a.attribute_code}>
                    {a.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="pim-field">
              <label>Image attribute</label>
              <select value={editing.attribute_as_image_code} onChange={(e) => setField('attribute_as_image_code', e.target.value)}>
                <option value="">(none)</option>
                {selectedAttrs
                  .filter((a) => a.type === 'image' || a.type === 'image_carousel')
                  .map((a) => (
                    <option key={a.attribute_code} value={a.attribute_code}>
                      {a.label}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <div className="pim-field">
            <label>Attributes &amp; requirements</label>
            <div className="pim-list-rows">
              {allAttributes.map((attr) => {
                const selected = inFamily(attr.code);
                const link = selectedAttrs.find((a) => a.attribute_code === attr.code);
                return (
                  <div className="pim-list-row" key={attr.code}>
                    <label className="pim-check grow">
                      <input type="checkbox" checked={selected} onChange={() => toggleAttr(attr)} />
                      <span>
                        {attr.label} <code style={{ fontSize: 12, color: '#889' }}>{attr.type}</code>
                      </span>
                    </label>
                    {selected && (
                      <>
                        <label className="pim-check">
                          <input
                            type="checkbox"
                            checked={link.is_required}
                            onChange={(e) => updateAttrFlag(attr.code, 'is_required', e.target.checked)}
                          />
                          required
                        </label>
                        <label className="pim-check">
                          <input
                            type="checkbox"
                            checked={link.is_required_per_channel}
                            onChange={(e) => updateAttrFlag(attr.code, 'is_required_per_channel', e.target.checked)}
                          />
                          per-region
                        </label>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Drawer>
      )}

      {confirmDelete && (
        <ConfirmModal
          title={`Delete ${confirmDelete.code}?`}
          body="Families with products cannot be deleted; reassign products first."
          danger
          confirmLabel="Delete"
          busy={saving}
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      <Toast message={toast?.message} error={toast?.error} />
    </>
  );
}
